import React, { useEffect, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import { VEHICLE_TYPES, FUEL_TYPES, EMPTY_VEHICLE_FORM } from "@/lib/parkingTypes";

const ALLOWED_MIME = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

export default function VehicleDialog({ open, onOpenChange, flatId, editing, onSaved }) {
    const [form, setForm] = useState(EMPTY_VEHICLE_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [photoUrl, setPhotoUrl] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setForm({
                vehicle_type: editing.vehicle_type || "car", registration_number: editing.registration_number || "",
                make: editing.make || "", model: editing.model || "", variant: editing.variant || "",
                color: editing.color || "", fuel_type: editing.fuel_type || "", registration_state: editing.registration_state || "",
                owner_name: editing.owner_name || "", owner_contact: editing.owner_contact || "",
                notes: editing.notes || "",
            });
            if (editing.photo_file_id) {
                api.get(`/parking-vehicles/${editing.id}/photo`, { responseType: "blob" })
                    .then((res) => setPhotoUrl(window.URL.createObjectURL(res.data)))
                    .catch(() => setPhotoUrl(null));
            } else setPhotoUrl(null);
        } else {
            setForm(EMPTY_VEHICLE_FORM);
            setPhotoUrl(null);
        }
    }, [open, editing]);

    function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }

    async function handlePhotoChosen(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !editing) return;
        if (!ALLOWED_MIME.includes(file.type)) {
            toast.error("Unsupported file type. Please upload a JPG, PNG or WEBP photo.");
            return;
        }
        if (file.size > MAX_BYTES) {
            toast.error("Photo is too large. Please upload a photo under 5 MB.");
            return;
        }
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/parking-vehicles/${editing.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Vehicle photo updated");
            const res = await api.get(`/parking-vehicles/${editing.id}/photo`, { responseType: "blob" });
            setPhotoUrl(window.URL.createObjectURL(res.data));
        } catch (err) { toast.error(formatError(err)); }
    }

    async function handleSave(e) {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const payload = { ...form };
            Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
            if (editing) {
                await api.patch(`/parking-vehicles/${editing.id}`, payload);
                toast.success("Vehicle updated");
            } else {
                await api.post(`/flats/${flatId}/parking-vehicles`, payload);
                toast.success("Vehicle added");
            }
            onOpenChange(false);
            onSaved();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">{editing ? "Edit vehicle" : "Add vehicle"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSave} className="space-y-4">
                    {editing && (
                        <div className="flex items-center gap-3">
                            <div className="w-16 h-16 rounded-sm bg-brand-bg border border-brand-line flex items-center justify-center overflow-hidden shrink-0">
                                {photoUrl ? <img src={photoUrl} alt="Vehicle" className="w-full h-full object-cover" /> : <Camera size={20} className="text-brand-inkSoft" />}
                            </div>
                            <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="vehicle-photo-input" onChange={handlePhotoChosen} />
                            <button type="button" data-testid="vehicle-photo-upload-btn" onClick={() => fileInputRef.current?.click()}
                                className="text-xs text-brand-action hover:underline">{photoUrl ? "Change photo" : "Add photo"}</button>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Vehicle type</Label>
                            <Select value={form.vehicle_type} onValueChange={(v) => set("vehicle_type", v)}>
                                <SelectTrigger data-testid="vehicle-type-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Registration number</Label>
                            <Input data-testid="vehicle-reg-input" required value={form.registration_number}
                                onChange={(e) => set("registration_number", e.target.value)} className="rounded-sm border-brand-line font-mono" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Make</Label>
                            <Input data-testid="vehicle-make-input" value={form.make} onChange={(e) => set("make", e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-2">
                            <Label>Model</Label>
                            <Input data-testid="vehicle-model-input" value={form.model} onChange={(e) => set("model", e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Variant (optional)</Label>
                            <Input data-testid="vehicle-variant-input" value={form.variant} onChange={(e) => set("variant", e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-2">
                            <Label>Colour</Label>
                            <Input data-testid="vehicle-color-input" value={form.color} onChange={(e) => set("color", e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Fuel type (optional)</Label>
                            <Select value={form.fuel_type || "__none"} onValueChange={(v) => set("fuel_type", v === "__none" ? "" : v)}>
                                <SelectTrigger data-testid="vehicle-fuel-type-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Not specified" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none">Not specified</SelectItem>
                                    {FUEL_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Registration state (optional)</Label>
                            <Input data-testid="vehicle-reg-state-input" value={form.registration_state} onChange={(e) => set("registration_state", e.target.value)} placeholder="e.g. Gujarat" className="rounded-sm border-brand-line" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Owner / driver name</Label>
                            <Input data-testid="vehicle-owner-input" value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-2">
                            <Label>Owner / driver contact</Label>
                            <Input data-testid="vehicle-owner-contact-input" value={form.owner_contact} onChange={(e) => set("owner_contact", e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Notes (optional)</Label>
                        <Textarea data-testid="vehicle-notes-input" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-sm border-brand-line" />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="vehicle-cancel-btn">Cancel</Button>
                        <Button type="submit" disabled={submitting} data-testid="vehicle-save-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                            {submitting ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
